import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { hash } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { eq, or } from 'tio-ui/utilities';
import StatCard from 'tio-reimagined/components/stat-card';
import SectionHeader from 'tio-reimagined/components/section-header';
import ContributionHero from 'tio-employee/components/reimagined/contribution-hero';
import {
  PhBank,
  PhTrendUp,
  PhCalendar,
  PhLightbulb,
  PhArrowRight,
  PhSparkle,
  PhCurrencyDollar,
  PhPercent,
  PhHandshake,
  PhTarget,
  PhChartLineUp,
  PhWarning,
  PhChatCircleDots,
  PhGraduationCap,
  PhMagnifyingGlass,
  PhBookOpen,
  PhCloudArrowUp,
  PhCheckCircle,
  PhCaretDown,
  PhCaretUp,
  PhPencilSimple,
  PhCurrencyCircleDollar,
  PhWarningCircle,
  PhLinkBreak,
  PhShieldSlash,
  PhMoneyWavy,
  PhProhibit,
} from 'ember-phosphor-icons';
import {
  getCurrentDemoClientData,
  getCurrentDemoScenario,
  getCurrentDemoClientId,
} from 'tio-employee/utils/career-pathways-demo-data';
import type RouterService from '@ember/routing/router-service';
import type CareerPathwaysApiService from 'tio-employee/services/career-pathways-api';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import { t, type IntlService } from 'ember-intl';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';
import MilestoneTracker from 'tio-employee/components/reimagined/milestone-tracker';
import type { DebtMilestone } from 'tio-employee/components/reimagined/milestone-tracker';
import SmartActionCard from 'tio-employee/components/reimagined/smart-action-card';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import NavCard from 'tio-reimagined/components/nav-card';
import type {
  LrapDiscoverySummary,
  EmployerContributions,
} from 'tio-employee/services/career-pathways-api';
import {
  type DataSourceInfo,
  type LiveSection127Info,
  type ContributionHistoryData,
  type ContributionActionItem,
  type ContributionYearlyPayment,
  type SlrPageState,
  normalizeSlrPageState,
  getActionItemIconBg as actionItemIconBg,
  getActionItemIconColor as actionItemIconColor,
} from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import AccountSelectorDrawer from 'tio-employee/components/reimagined/account-selector-drawer';
import DocumentUploadDrawer from 'tio-employee/components/reimagined/document-upload-drawer';
import BenefitDetailDrawer from 'tio-employee/components/reimagined/benefit-detail-drawer';
import IssueNotificationCard from 'tio-employee/components/reimagined/issue-notification-card';
import AddressSelectionCard from 'tio-employee/components/reimagined/address-selection-card';
import GroupedIssueNotificationCard from 'tio-employee/components/reimagined/grouped-issue-notification-card';
import type AccountModel from 'tio-data/models/account';
import type StoreService from 'ember-data/store';

interface LoansIndexModel {
  loans: LoanAccount[];
  hasLinkedLoans: boolean;
  isDemoMode: boolean;
  dataSourceInfo: DataSourceInfo;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  section127Info: LiveSection127Info;
  hasStrategyFinderResults: boolean;
  activeAccounts: AccountModel[];
  accountReceivingContributions: AccountModel | null;
  personId: string | null;
  contributionHistory?: ContributionHistoryData;
}

interface LoansIndexSignature {
  Args: {
    model: LoansIndexModel;
  };
}

// Demo loan data with contribution tracking
interface LoanAccountWithContributions extends LoanAccount {
  amountReceived?: number;
  contributionCount?: number;
}

const DEMO_LOANS: LoanAccountWithContributions[] = [
  {
    id: '1',
    name: 'Federal Direct Loan',
    accountNumber: '****4821',
    servicer: 'MOHELA',
    balance: 32450,
    originalBalance: 48000,
    interestRate: 5.2,
    monthlyPayment: 285,
    nextPaymentDate: '2026-02-15',
    estimatedPayoff: '2028-03-15',
    isReceivingEmployerHelp: true,
    loanCount: 4,
    accountType: 'federal',
    paymentAddress: '123 Main St, Springfield, IL 62704',
    amountReceived: 800,
    contributionCount: 8,
  },
  {
    id: '2',
    name: 'Federal Stafford Loan',
    accountNumber: '****7293',
    servicer: 'Nelnet',
    balance: 12780,
    originalBalance: 22000,
    interestRate: 6.8,
    monthlyPayment: 102,
    nextPaymentDate: '2026-02-20',
    estimatedPayoff: '2029-08-15',
    isReceivingEmployerHelp: false,
    loanCount: 2,
    accountType: 'federal',
    paymentAddress: '456 Oak Ave, Lincoln, NE 68508',
    amountReceived: 0,
    contributionCount: 0,
  },
];

// Demo milestones
const DEMO_MILESTONES: DebtMilestone[] = [
  {
    id: '1',
    title: 'First $5k',
    description: 'Paid first $5,000',
    threshold: 7,
    achieved: true,
    achievedDate: 'Mar 2024',
    icon: 'rocket',
  },
  {
    id: '2',
    title: '25% Done',
    description: 'Quarter paid off',
    threshold: 25,
    achieved: true,
    achievedDate: 'Sep 2024',
    icon: 'star',
  },
  {
    id: '3',
    title: 'Halfway',
    description: '50% to freedom',
    threshold: 50,
    achieved: false,
    icon: 'target',
  },
  {
    id: '4',
    title: 'Under $20k',
    description: 'Below $20,000',
    threshold: 71,
    achieved: false,
    icon: 'medal',
  },
  {
    id: '5',
    title: 'Debt Free!',
    description: '$0 balance',
    threshold: 100,
    achieved: false,
    icon: 'crown',
  },
];

// Smart actions for debt management - static structure, dynamic content via getter
const createDebtActions = (options: {
  section127Remaining: number;
  section127Used: number;
  hasStrategyFinderResults: boolean;
  formatCurrency: (amount: number) => string;
}): SmartAction[] => {
  const { section127Remaining, section127Used, hasStrategyFinderResults, formatCurrency } = options;

  const actions: SmartAction[] = [];

  // Strategy Finder action - higher priority if user hasn't run it
  actions.push({
    id: '1',
    priority: hasStrategyFinderResults ? 'medium' : 'high',
    category: 'money',
    icon: PhPercent,
    title: hasStrategyFinderResults
      ? 'Update your repayment strategy'
      : 'Find your optimal repayment strategy',
    description: hasStrategyFinderResults
      ? "Life changes. Review your strategy to ensure you're still on the best path to debt freedom."
      : "Based on your employment and loan profile, we can find the best path to debt freedom—whether that's PSLF, IDR, or aggressive payoff.",
    impact: 'Potentially save thousands',
    cta: hasStrategyFinderResults ? 'Review Strategy' : 'Run Strategy Finder',
    route: 'authenticated.repaying-student-debt.repayment-strategy-finder',
  });

  // Section 127 action - only show if there's remaining balance
  if (section127Remaining > 0) {
    actions.push({
      id: '2',
      priority: section127Remaining > 1000 ? 'high' : 'medium',
      category: 'opportunity',
      icon: PhHandshake,
      title: `${formatCurrency(section127Remaining)} of Section 127 unused`,
      description: `You've used ${formatCurrency(section127Used)} of your $5,250 tax-free benefit this year. Allocating the rest to loans could accelerate payoff.`,
      impact: 'Pay off faster',
      cta: 'Optimize Allocation',
      route: 'authenticated.reimagined.finance.optimizer',
    });
  }

  // AI Advisor action
  actions.push({
    id: '3',
    priority: 'medium',
    category: 'opportunity',
    icon: PhSparkle,
    title: 'Ask the AI Loan Advisor',
    description:
      'Get personalized guidance on PSLF, IDR plans, refinancing decisions, and repayment optimization from our AI expert.',
    impact: 'Expert advice instantly',
    cta: 'Chat with Advisor',
    route: 'authenticated.reimagined.plan.advisor',
  });

  return actions;
};

export default class CareerPathwaysLoansIndex extends Component<LoansIndexSignature> {
  @service('career-pathways-api') declare careerPathwaysApi: CareerPathwaysApiService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @tracked expandedAccounts = new Set<string>();
  @tracked isAccountDrawerOpen = false;
  @tracked isUploadDrawerOpen = false;
  @tracked isBenefitDrawerOpen = false;

  get person() {
    return this.sessionContext.person;
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get dataSourceInfo() {
    return this.args.model.dataSourceInfo;
  }

  /**
   * Data source for student loan sections
   */
  get studentLoansSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.studentLoans === 'live' ? 'live' : 'placeholder';
  }

  /**
   * Data source for employer contributions
   */
  get contributionsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.employerContributions === 'live' ? 'live' : 'placeholder';
  }

  /**
   * Data source for LRAP discovery
   */
  get lrapSource(): DataSource {
    // LRAP discovery is currently demo/placeholder
    return this.isDemoMode ? 'demo' : 'placeholder';
  }

  /**
   * Data source for AI recommendations/smart actions
   */
  get recommendationsSource(): DataSource {
    // AI recommendations are placeholder until AI service built
    return this.isDemoMode ? 'demo' : 'placeholder';
  }

  /**
   * Data source for debt milestones
   */
  get milestonesSource(): DataSource {
    // Milestones are calculated from loan data
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.studentLoans === 'live' ? 'live' : 'placeholder';
  }

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get scenario() {
    return getCurrentDemoScenario();
  }

  get showLoans() {
    return this.isDemoMode ? this.scenario.features.studentLoanRepayment : true;
  }

  get isFirstLogin() {
    return this.isDemoMode ? this.scenario.userState.isFirstLogin : false;
  }

  get hasLinkedLoans() {
    return this.isDemoMode
      ? this.scenario.userState.hasLinkedLoans
      : this.args.model.hasLinkedLoans;
  }

  get loans(): LoanAccountWithContributions[] {
    return this.isDemoMode ? DEMO_LOANS : this.args.model.loans;
  }

  get milestones(): DebtMilestone[] {
    // In demo mode, use static milestones
    if (this.isDemoMode) {
      return DEMO_MILESTONES;
    }

    // Calculate milestones dynamically from real loan data
    const totalOriginal = this.totalOriginalBalance;
    const totalCurrent = this.totalBalance;

    if (totalOriginal <= 0) {
      return DEMO_MILESTONES;
    }

    const paidOff = totalOriginal - totalCurrent;
    const paidPercent = (paidOff / totalOriginal) * 100;

    return [
      {
        id: '1',
        title: 'First $5k',
        description: 'Paid first $5,000',
        threshold: totalOriginal > 5000 ? (5000 / totalOriginal) * 100 : 50,
        achieved: paidOff >= 5000,
        achievedDate: paidOff >= 5000 ? 'Achieved!' : undefined,
        icon: 'rocket',
      },
      {
        id: '2',
        title: '25% Done',
        description: 'Quarter paid off',
        threshold: 25,
        achieved: paidPercent >= 25,
        achievedDate: paidPercent >= 25 ? 'Achieved!' : undefined,
        icon: 'star',
      },
      {
        id: '3',
        title: 'Halfway',
        description: '50% to freedom',
        threshold: 50,
        achieved: paidPercent >= 50,
        icon: 'target',
      },
      {
        id: '4',
        title: '75% Done',
        description: 'Three quarters there',
        threshold: 75,
        achieved: paidPercent >= 75,
        icon: 'medal',
      },
      {
        id: '5',
        title: 'Debt Free!',
        description: '$0 balance',
        threshold: 100,
        achieved: totalCurrent <= 0,
        icon: 'crown',
      },
    ];
  }

  get section127Info() {
    return this.args.model.section127Info;
  }

  get hasStrategyFinderResults() {
    return this.isDemoMode ? false : this.args.model.hasStrategyFinderResults;
  }

  get actions() {
    // In demo mode, use demo values
    if (this.isDemoMode) {
      return createDebtActions({
        section127Remaining: 1200,
        section127Used: 4050,
        hasStrategyFinderResults: false,
        formatCurrency: formatCurrency,
      });
    }

    // Use live data
    return createDebtActions({
      section127Remaining: this.section127Info.remaining,
      section127Used: this.section127Info.yearToDateUsed,
      hasStrategyFinderResults: this.hasStrategyFinderResults,
      formatCurrency: formatCurrency,
    });
  }

  get totalBalance() {
    return this.loans.reduce((sum, loan) => sum + loan.balance, 0);
  }

  get totalOriginalBalance() {
    return this.loans.reduce((sum, loan) => sum + loan.originalBalance, 0);
  }

  get totalMonthlyPayment() {
    return this.loans.reduce((sum, loan) => sum + loan.monthlyPayment, 0);
  }

  get progressPercent() {
    if (this.totalOriginalBalance <= 0) return 0;
    const paid = this.totalOriginalBalance - this.totalBalance;
    return Math.round((paid / this.totalOriginalBalance) * 100);
  }

  get yearProgressAmount() {
    if (this.isDemoMode) return 4850;
    // For live data, estimate YTD progress from employer contributions
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      // Include employer contributions as part of year progress
      return contributions.contributions.yearToDate;
    }
    const paid = this.totalOriginalBalance - this.totalBalance;
    return paid > 0 ? paid : 0;
  }

  get yearOverYearImprovement() {
    // Calculate year-over-year improvement percentage
    if (this.isDemoMode) return 34;
    // For live data, estimate based on progress
    if (this.totalOriginalBalance <= 0) return 0;
    const progressPct = this.progressPercent;
    // Estimate improvement based on how much faster we're paying off vs original schedule
    return Math.min(Math.round(progressPct * 0.5), 50);
  }

  get estimatedPayoffDate() {
    if (this.isDemoMode) return '2029-08-15';
    // Calculate from loan data
    if (this.loans.length === 0) return new Date().toISOString();
    // Find the latest estimated payoff from all loans
    const payoffDates = this.loans
      .map((loan) => loan.estimatedPayoff)
      .filter((date) => date)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    return payoffDates[0] || new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000).toISOString();
  }

  get averageInterestRate() {
    if (!this.loans.length) return 0;
    const weighted = this.loans.reduce((sum, loan) => sum + loan.balance * loan.interestRate, 0);
    const total = this.loans.reduce((sum, loan) => sum + loan.balance, 0);
    return total > 0 ? Number((weighted / total).toFixed(1)) : 0;
  }

  get nextPaymentSubtext() {
    if (this.isDemoMode) return 'Next due: Feb 15';
    const next = this.loans
      .map((loan) => loan.nextPaymentDate)
      .filter((date) => date)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
    if (!next) return 'Next due: —';
    const formatted = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(new Date(next));
    return `Next due: ${formatted}`;
  }

  get employerName() {
    if (this.isDemoMode) return this.demoClient.client.name;
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  // LRAP Discovery Integration
  get lrapDiscovery(): LrapDiscoverySummary | null {
    if (!this.isDemoMode) return null;
    // Fetch LRAP discovery data for banner
    const clientId = getCurrentDemoClientId();
    // Only show LRAP banner for certain demo clients
    if (clientId === 'legal' || clientId === 'advocate') {
      return this.careerPathwaysApi.fetchLrapDiscoverySyncForDemo();
    }
    return null;
  }

  get hasLrapOpportunity() {
    const discovery = this.lrapDiscovery;
    if (!discovery) return false;
    return (
      discovery.eligiblePrograms.length > 0 ||
      discovery.enrolledPrograms.length > 0 ||
      discovery.totalPotentialAnnualValue > 0
    );
  }

  get potentialLrapAmount() {
    return this.lrapDiscovery?.totalPotentialAnnualValue ?? 0;
  }

  get lrapEligibleCount() {
    return this.lrapDiscovery?.eligiblePrograms.length ?? 0;
  }

  get lrapEnrolledCount() {
    return this.lrapDiscovery?.enrolledPrograms.length ?? 0;
  }

  get hasOutsideFundsConflicts() {
    return (this.lrapDiscovery?.outsideFundsConflicts.length ?? 0) > 0;
  }

  /**
   * Monthly employer contribution amount - live from plan data
   */
  get monthlyContribution() {
    if (this.isDemoMode) return 125;
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      return Math.round(contributions.contributions.projectedAnnual / 12) || 0;
    }
    return 0;
  }

  get annualContribution() {
    return this.monthlyContribution * 12;
  }

  get waitingPeriodDate() {
    const opts = {
      month: 'long' as const,
      day: 'numeric' as const,
      year: 'numeric' as const,
    };
    if (this.isDemoMode) {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 60);
      return this.intl.formatDate(futureDate, opts);
    }
    const dateStr = this.sessionContext.currentEmployee?.eligibilityStartDate;
    return dateStr ? this.intl.formatDate(new Date(dateStr), opts) : undefined;
  }

  /**
   * Lifetime employer benefit amount - live from contribution summary
   */
  get lifetimeBenefit() {
    if (this.isDemoMode) return 18000;
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      return contributions.contributions.lifetime || 0;
    }
    return 0;
  }

  get contributionYears(): ContributionYearlyPayment[] {
    return this.args.model.contributionHistory?.paymentsByYear ?? [];
  }

  /**
   * Action items requiring user attention — works for both demo and live users.
   *
   * In demo mode, use `?demo_alert=account-setup|account-ineligible|plan-requirement|default|
   * partially-applied|blank-account-number|no-loan-balance|blank-payment-address|all|none`
   * to preview each issue type icon.
   *
   * In live mode, action items come from the API's SlrAlertService (preferred)
   * or from client-side accountAlerts + validation errors (fallback).
   *
   * Figma ref: Issue Type Icons (node 1342:5380)
   */
  get actionItems(): ContributionActionItem[] {
    if (this.isDemoMode) {
      const params = new URLSearchParams(window.location.search);
      const alertParam = params.get('demo_alert') ?? 'all';

      const allDemoAlerts: ContributionActionItem[] = [
        {
          id: 'slr-grouped-account-info',
          title: this.intl.t('slr.reimagined.alert_grouped_account_info_title'),
          description: this.intl.t('slr.reimagined.alert_grouped_account_info_desc_mixed'),
          badge: this.intl.t('slr.reimagined.action_act_now'),
          badgeIntent: 'warning',
          route: 'authenticated.reimagined.loans',
          issueType: 'account-setup',
          tasks: [
            {
              id: 'upload-loan-information',
              title: this.intl.t('slr.reimagined.grouped_upload_task_title'),
              description: this.intl.t(
                'slr.reimagined.grouped_upload_task_desc_account_and_balance'
              ),
              resolutionType: 'upload',
              issueLabels: [
                this.intl.t('slr.reimagined.alert_blank_account_number_label'),
                this.intl.t('slr.reimagined.alert_no_loan_balance_label'),
              ],
              successMessage: this.intl.t('slr.reimagined.successfully_updated_loan_information'),
            },
            {
              id: 'blank-payment-address',
              title: this.intl.t('slr.reimagined.grouped_address_task_title'),
              description: this.intl.t('slr.reimagined.alert_blank_payment_address_desc_dropdown'),
              resolutionType: 'dropdown',
              issueLabels: [this.intl.t('slr.reimagined.alert_blank_payment_address_label')],
              metadata: {
                available_addresses: [
                  { id: 'addr-1', label: '9876 north ave City, State 98765' },
                  { id: 'addr-2', label: '1234 main st City, State 12345' },
                  { id: 'addr-3', label: '333 Washington blvd City, State 33333' },
                ],
              },
            },
          ],
          successMessage: this.intl.t('slr.reimagined.successfully_updated_loan_information'),
        },
        {
          id: 'slr-all-pif',
          title: this.intl.t('slr.reimagined.alert_all_pif_title'),
          description: this.intl.t('slr.reimagined.alert_all_pif_desc'),
          badge: this.intl.t('slr.reimagined.action_action_needed'),
          badgeIntent: 'danger',
          route: 'authenticated.observability.upload',
          issueType: 'account-setup',
          resolutionType: 'upload',
        },
        {
          id: 'slr-partially-applied',
          title: this.intl.t('slr.reimagined.alert_partially_applied_title'),
          description: this.intl.t('slr.reimagined.alert_partially_applied_desc'),
          badge: this.intl.t('slr.reimagined.action_action_needed'),
          badgeIntent: 'danger',
          route: 'authenticated.reimagined.loans',
          issueType: 'account-setup',
          resolutionType: 'contact-support',
        },
        {
          id: 'slr-blank-account-number',
          title: this.intl.t('slr.reimagined.alert_blank_account_number_title'),
          description: this.intl.t('slr.reimagined.alert_blank_account_number_desc'),
          badge: this.intl.t('slr.reimagined.action_act_now'),
          badgeIntent: 'warning',
          route: 'authenticated.observability.upload',
          issueType: 'account-setup',
          resolutionType: 'upload',
        },
        {
          id: 'slr-no-loan-balance',
          title: this.intl.t('slr.reimagined.alert_no_loan_balance_title'),
          description: this.intl.t('slr.reimagined.alert_no_loan_balance_desc'),
          badge: this.intl.t('slr.reimagined.action_act_now'),
          badgeIntent: 'warning',
          route: 'authenticated.observability.upload',
          issueType: 'account-setup',
          resolutionType: 'upload',
        },
        {
          id: 'slr-blank-payment-address',
          title: this.intl.t('slr.reimagined.alert_blank_payment_address_title'),
          description: this.intl.t('slr.reimagined.alert_blank_payment_address_desc_dropdown'),
          badge: this.intl.t('slr.reimagined.action_act_now'),
          badgeIntent: 'warning',
          route: 'authenticated.reimagined.loans',
          issueType: 'account-setup',
          resolutionType: 'dropdown',
          metadata: {
            available_addresses: [
              { id: 'addr-1', label: '9876 north ave City, State 98765' },
              { id: 'addr-2', label: '1234 main st City, State 12345' },
              { id: 'addr-3', label: '333 Washington blvd City, State 33333' },
            ],
          },
        },
        {
          id: 'upload-required',
          title: this.intl.t('slr.reimagined.action_update_loan_title'),
          description: this.intl.t('slr.reimagined.action_upload_loan_doc'),
          badge: this.intl.t('slr.reimagined.action_act_now'),
          badgeIntent: 'danger',
          route: 'authenticated.observability.upload',
          issueType: 'account-setup',
        },
        {
          id: 'validation-INVALID_LOAN_TYPE',
          title: this.intl.t('slr.reimagined.action_account_ineligible_title'),
          description: this.intl.t('slr.reimagined.action_account_ineligible_desc'),
          badge: this.intl.t('slr.reimagined.action_required_for_payment'),
          badgeIntent: 'danger',
          route: 'authenticated.observability.upload',
          issueType: 'account-ineligible',
        },
        {
          id: 'plan-ineligible',
          title: this.intl.t('slr.reimagined.action_plan_issue_title'),
          description: this.intl.t('slr.reimagined.action_plan_issue'),
          badge: this.intl.t('slr.reimagined.action_action_needed'),
          badgeIntent: 'danger',
          route: 'authenticated.observability.upload',
          issueType: 'plan-requirement',
        },
        {
          id: 'payment-error',
          title: this.intl.t('slr.reimagined.action_resolve_error_title'),
          description: this.intl.t('slr.reimagined.action_resolve_error'),
          badge: this.intl.t('slr.reimagined.action_needs_review'),
          badgeIntent: 'warning',
          route: 'authenticated.reimagined.loans',
          issueType: 'default',
          resolutionType: 'contact-support',
        },
      ];

      if (alertParam === 'none') return [];
      if (alertParam === 'all') return allDemoAlerts;
      // Filter by issueType, resolutionType, or SLR alert type (from the item id after 'slr-')
      return allDemoAlerts.filter(
        (a) =>
          a.issueType === alertParam ||
          a.resolutionType === alertParam ||
          a.id === `slr-${alertParam}`
      );
    }

    return this.args.model.contributionHistory?.actionItems ?? [];
  }

  getActionItemIconBg = (item: ContributionActionItem) => actionItemIconBg(item);

  getActionItemIconColor = (item: ContributionActionItem) => actionItemIconColor(item);

  /**
   * Hero section metrics — demo mode uses static values,
   * live mode computes from employer contributions data.
   * Variant is determined by plan limits (annualLimit / lifetimeLimit in cents).
   */
  get heroProps() {
    if (this.isDemoMode) {
      return {
        variant: 2 as 1 | 2 | 3 | 4,
        annualUsage: 0,
        annualLimit: 0,
        annualProgressPercent: 0,
        lifetimeUsage: 4800,
        lifetimeLimit: 12000,
        lifetimeProgressPercent: 40,
        lifetimeContribution: 4800,
        lifetimeAsOfDate: '01/2023',
        yearContribution: 800,
        currentYear: new Date().getFullYear(),
        paymentCount: 4,
        lastPaymentAmount: 125,
        lastPaymentDate: '02/2026',
        nextContribution: 200,
        nextContributionDate: '01/2026',
        blocked: false,
      };
    }

    const plan = this.sessionContext.currentEmployee?.plan;
    const annualLimitCents = plan?.annualLimit ?? 0;
    const lifetimeLimitCents = plan?.lifetimeLimit ?? 0;
    const annualLimitDollars = annualLimitCents / 100;
    const lifetimeLimitDollars = lifetimeLimitCents / 100;

    const contributions = this.args.model.employerContributions;
    const isLive = contributions.isLiveData;
    const yearToDate = isLive ? contributions.contributions.yearToDate : 0;
    const lifetime = isLive ? contributions.contributions.lifetime : 0;

    const now = new Date();

    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextDate = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      year: 'numeric',
    }).format(nextMonth);

    let variant: 1 | 2 | 3 | 4;
    if (annualLimitCents > 0 && lifetimeLimitCents > 0) {
      variant = 4;
    } else if (annualLimitCents > 0) {
      variant = 1;
    } else if (lifetimeLimitCents > 0) {
      variant = 2;
    } else {
      variant = 3;
    }

    const annualProgressPercent =
      annualLimitDollars > 0
        ? Math.min(Math.round((yearToDate / annualLimitDollars) * 100), 100)
        : 0;
    const lifetimeProgressPercent =
      lifetimeLimitDollars > 0
        ? Math.min(Math.round((lifetime / lifetimeLimitDollars) * 100), 100)
        : 0;

    const projected = isLive ? contributions.contributions.projectedAnnual : 0;
    const monthly = Math.round(projected / 12) || 0;

    const payments = this.args.model.contributionHistory?.payments;
    const lastPayment = payments?.length ? payments[0] : undefined;
    const lastPaymentAmount = lastPayment?.amount ?? 0;
    const lastPaymentDate = lastPayment?.paidDate
      ? (() => {
          const d = new Date(lastPayment.paidDate);
          return isNaN(d.getTime())
            ? ''
            : this.intl.formatDate(d, { month: '2-digit', year: 'numeric' });
        })()
      : '';

    return {
      variant,
      annualUsage: yearToDate,
      annualLimit: annualLimitDollars,
      annualProgressPercent,
      lifetimeUsage: lifetime,
      lifetimeLimit: lifetimeLimitDollars,
      lifetimeProgressPercent,
      lifetimeContribution: lifetime,
      lifetimeAsOfDate: this.formatOldestPaymentDate(),
      yearContribution: yearToDate,
      currentYear: now.getFullYear(),
      paymentCount: this.paymentCountForCurrentYear,
      lastPaymentAmount,
      lastPaymentDate,
      nextContribution: monthly,
      nextContributionDate: nextDate,
      blocked: this.isContributionBlocked,
    };
  }

  formatOldestPaymentDate() {
    const payments = this.args.model.contributionHistory?.payments;
    if (!payments?.length) return '';
    const oldest = payments[payments.length - 1];
    const dateStr = oldest?.paidDate;
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return this.intl.formatDate(date, {
      month: '2-digit',
      year: 'numeric',
    });
  }

  get paymentCountForCurrentYear() {
    const currentYear = String(new Date().getFullYear());
    const yearData = this.args.model.contributionHistory?.paymentsByYear?.find(
      (y) => y.year === currentYear
    );
    return yearData?.paymentCount ?? 0;
  }

  get receivingLoans() {
    return this.loans.filter((loan) => loan.isReceivingEmployerHelp);
  }

  get otherLoans() {
    return this.loans.filter((loan) => !loan.isReceivingEmployerHelp);
  }

  toggleAccountExpansion = (accountId: string) => {
    if (this.expandedAccounts.has(accountId)) {
      this.expandedAccounts.delete(accountId);
    } else {
      this.expandedAccounts.add(accountId);
    }
    this.expandedAccounts = new Set(this.expandedAccounts);
  };

  isAccountExpanded = (accountId: string) => {
    return this.expandedAccounts.has(accountId);
  };

  toggleAccountDrawer = () => {
    this.isAccountDrawerOpen = !this.isAccountDrawerOpen;
  };

  toggleUploadDrawer = () => {
    this.isUploadDrawerOpen = !this.isUploadDrawerOpen;
  };

  openBenefitDrawer = () => {
    this.isBenefitDrawerOpen = true;
  };

  closeBenefitDrawer = () => {
    this.isBenefitDrawerOpen = false;
  };

  get plan() {
    if (this.isDemoMode) {
      return {
        name: 'Acme Student Loan Repayment',
        description: '',
        cadence: 'MONTHLY',
        milestoneDate: 'ELIGIBILITY_DATE',
        waitingPeriod: 3,
        waitingPeriodUnit: 'months',
        tiers: [{ 'from-cycle': 1, per: 0, 'override-amount': '', amount: 12500 }],
        annualLimit: 150000,
        lifetimeLimit: 1200000,
      } as import('tio-data/models/plan').default;
    }
    return this.sessionContext.currentEmployee?.plan ?? null;
  }

  handleActionItemClick = (item: ContributionActionItem) => {
    if (item.resolutionType === 'upload') {
      this.isUploadDrawerOpen = true;
      return;
    }
    if (item.resolutionType === 'dropdown') {
      this.isAccountDrawerOpen = true;
    }
  };

  getAddressOptions = (item: ContributionActionItem) => {
    const addresses = (item.metadata?.available_addresses ?? []) as Array<{
      id: string;
      label: string;
    }>;
    return addresses;
  };

  handleAddressSubmit = async (_item: ContributionActionItem, addressId: string) => {
    if (this.isDemoMode) return;

    const account = this.args.model.accountReceivingContributions;
    if (!account) throw new Error('No contribution account found');

    const postalAddress = await this.store.findRecord('postal-address', addressId);
    // @ts-expect-error postalAddress relationship assignment
    account.postalAddress = postalAddress;
    await account.save();
    this.router.refresh('authenticated.reimagined.loans');
  };

  onAccountChanged = () => {
    this.router.refresh('authenticated.reimagined.loans');
  };

  get firstName() {
    return (
      this.sessionContext.user?.person?.firstName || this.intl.t('slr.reimagined.default_name')
    );
  }

  get greeting() {
    const hour = new Date().getHours();
    if (hour < 12) return this.intl.t('slr.reimagined.good_morning');
    if (hour < 17) return this.intl.t('slr.reimagined.good_afternoon');
    return this.intl.t('slr.reimagined.good_evening');
  }

  /**
   * Page state from the SLR alert service — determines which hero variant
   * and which sections are visible on the page.
   *
   * In demo mode, use `?page_state=<state>` to preview.
   */
  get pageState(): SlrPageState | undefined {
    if (this.isDemoMode) {
      const params = new URLSearchParams(window.location.search);
      const param = params.get('page_state');
      if (param) return normalizeSlrPageState(param) ?? 'active';
      return 'active';
    }
    return this.args.model.contributionHistory?.pageState;
  }

  /** Whether contributions are blocked (limit reached, PIF, or validation errors) */
  get isContributionBlocked() {
    const ps = this.pageState;
    return (
      ps === 'all-pif' ||
      ps === 'limit-annual' ||
      ps === 'limit-lifetime' ||
      ps === 'limit-annual-and-lifetime' ||
      (this.args.model.contributionHistory?.paymentValidationErrors?.length ?? 0) > 0
    );
  }

  /** Whether to show the full dashboard (stats, smart actions, milestones, financial tools) */
  get showFullDashboard() {
    const ps = this.pageState;
    return ps === 'active' || ps === undefined;
  }

  /** Whether to show loan accounts section */
  get showLoanAccounts() {
    const ps = this.pageState;
    return (
      ps === 'active' ||
      ps === undefined ||
      ps === 'all-pif' ||
      ps === 'limit-annual' ||
      ps === 'limit-lifetime' ||
      ps === 'limit-annual-and-lifetime' ||
      ps === 'non-slr-former' ||
      ps === 'waiting-period'
    );
  }

  /** Whether to show contribution history section */
  get showContributionHistory() {
    const ps = this.pageState;
    return (
      ps === 'active' ||
      ps === undefined ||
      ps === 'all-pif' ||
      ps === 'limit-annual' ||
      ps === 'limit-lifetime' ||
      ps === 'limit-annual-and-lifetime' ||
      ps === 'non-slr-former' ||
      ps === 'waiting-period'
    );
  }

  /** Whether to show action items (only in active state or when alerts exist) */
  get showActionItems() {
    const ps = this.pageState;
    return ps === 'active' || ps === undefined || this.actionItems.length > 0;
  }

  /** Info banner content for blocked states (PIF, limit reached) */
  get pageStateInfoBanner(): {
    title: string;
    description: string;
    icon: 'check' | 'prohibit';
  } | null {
    const ps = this.pageState;
    if (ps === 'all-pif') {
      return {
        title: this.intl.t('slr.reimagined.page_state_all_pif_title'),
        description: this.intl.t('slr.reimagined.page_state_all_pif_desc'),
        icon: 'check',
      };
    }
    if (ps === 'limit-annual') {
      return {
        title: this.intl.t('slr.reimagined.page_state_limit_annual_title'),
        description: this.intl.t('slr.reimagined.page_state_limit_annual_desc'),
        icon: 'prohibit',
      };
    }
    if (ps === 'limit-lifetime') {
      return {
        title: this.intl.t('slr.reimagined.page_state_limit_lifetime_title'),
        description: this.intl.t('slr.reimagined.page_state_limit_lifetime_desc'),
        icon: 'prohibit',
      };
    }
    if (ps === 'limit-annual-and-lifetime') {
      return {
        title: this.intl.t('slr.reimagined.page_state_limit_both_title'),
        description: this.intl.t('slr.reimagined.page_state_limit_both_desc'),
        icon: 'prohibit',
      };
    }
    return null;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{#if (eq this.pageState "non-slr-never")}}
        {{! NON-SLR USER — never had SLR }}
        <div class="tio-gradient-brand-soft rounded-3xl p-6 overflow-hidden">
          <Heading @level={{1}} class="app-page-title mb-2">
            {{t "slr.reimagined.page_state_not_available_title"}}
          </Heading>
          <Text class="tio-fg-secondary">
            {{t "slr.reimagined.page_state_not_available_desc"}}
          </Text>
        </div>

      {{else if (eq this.pageState "no-accounts")}}
        {{! NO ACCOUNTS — eligible user, needs to add loan account }}
        <div class="tio-gradient-brand-soft rounded-3xl p-6 overflow-hidden">
          <div class="tio-row-start-between mb-6">
            <Heading @level={{1}} class="app-page-title">
              {{t "slr.reimagined.page_state_not_available_title"}}
            </Heading>
            <button
              type="button"
              class="text-base font-semibold tio-fg-primary hover:tio-fg-brand underline decoration-solid"
              {{on "click" this.openBenefitDrawer}}
            >
              {{t "slr.reimagined.benefit_details_link"}}
            </button>
          </div>
          <div class="tio-row-start-between">
            <div class="tio-col-loose flex-1">
              <Text class="tio-fg-secondary max-w-lg">
                {{t "slr.reimagined.page_state_no_accounts_desc"}}
              </Text>
              <div>
                <LinkTo
                  @route="authenticated.observability.upload"
                  class="tio-inline-row-center gap-2 rounded-md bg-gray-900 px-4 py-2.5 text-sm font-semibold tio-fg-inverse hover:bg-gray-800"
                >
                  {{t "slr.reimagined.page_state_no_accounts_cta"}}
                </LinkTo>
              </div>
            </div>
            <div class="app-stat-card app-stat-card--on-hero tio-col w-60 shrink-0">
              <Text @weight="medium" class="tio-fg-secondary">
                {{t "slr.reimagined.page_state_eligible_for"}}
              </Text>
              <div class="flex items-baseline gap-0.5">
                <span class="text-3xl font-semibold tio-fg-primary">
                  {{formatCurrency this.monthlyContribution}}
                </span>
                <span class="text-base tio-fg-secondary">/month</span>
              </div>
              <Text @size="sm" class="tio-fg-muted">
                {{t
                  "slr.reimagined.page_state_eligible_annually"
                  amount=(formatCurrency this.annualContribution)
                }}
              </Text>
            </div>
          </div>
        </div>

      {{else if this.showLoans}}
        {{#if this.hasLinkedLoans}}
          {{! Hero Section — varies by page state }}
          {{#if (eq this.pageState "non-slr-former")}}
            <div class="tio-gradient-brand-soft rounded-3xl p-6 overflow-hidden">
              <Heading @level={{1}} class="app-page-title mb-2">
                {{t "slr.reimagined.page_state_not_available_title"}}
              </Heading>
              <Text class="tio-fg-secondary">
                {{t "slr.reimagined.page_state_not_available_desc"}}
              </Text>
            </div>
          {{else if (eq this.pageState "waiting-period")}}
            <div class="tio-gradient-brand-soft rounded-3xl p-6 overflow-hidden">
              <div class="tio-row-start-between mb-1">
                <div class="tio-row-tight">
                  <PhMoneyWavy @size={{18}} class="tio-fg-secondary" />
                  <Text @size="sm" class="tio-fg-secondary">
                    {{this.greeting}},
                    {{this.firstName}}
                  </Text>
                </div>
                <button
                  type="button"
                  class="text-base font-semibold tio-fg-primary hover:tio-fg-brand underline decoration-solid"
                  {{on "click" this.openBenefitDrawer}}
                >
                  {{t "slr.reimagined.benefit_details_link"}}
                </button>
              </div>
              <Heading @level={{1}} class="app-page-title mb-4">
                {{t "slr.reimagined.page_state_waiting_title"}}
              </Heading>
              <div class="tio-row-start-between gap-4">
                <div class="tio-bg-surface border tio-border-default rounded-2xl p-4 flex-1">
                  <Heading @level={{3}} class="mb-1">
                    {{t
                      "slr.reimagined.page_state_waiting_eligibility"
                      date=this.waitingPeriodDate
                    }}
                  </Heading>
                  <Text @size="sm" class="tio-fg-secondary mb-3">
                    {{t "slr.reimagined.page_state_waiting_desc"}}
                  </Text>
                  <LinkTo
                    @route="authenticated.observability.upload"
                    class="tio-inline-row-center gap-2 rounded-md bg-gray-900 px-4 py-2.5 text-sm font-semibold tio-fg-inverse hover:bg-gray-800"
                  >
                    {{t "slr.reimagined.page_state_no_accounts_cta"}}
                  </LinkTo>
                </div>
                <div class="app-stat-card app-stat-card--on-hero tio-col w-60 shrink-0">
                  <Text @weight="medium" class="tio-fg-secondary">
                    {{t "slr.reimagined.page_state_soon_eligible"}}
                  </Text>
                  <div class="flex items-baseline gap-0.5">
                    <span class="text-3xl font-semibold tio-fg-primary">
                      {{formatCurrency this.monthlyContribution}}
                    </span>
                    <span class="text-base tio-fg-secondary">/month</span>
                  </div>
                  <Text @size="sm" class="tio-fg-muted">
                    {{t "slr.reimagined.page_state_starting_date" date=this.waitingPeriodDate}}
                  </Text>
                </div>
              </div>
            </div>
          {{else}}
            <DataIndicator
              @source={{this.contributionsSource}}
              @showBadge={{true}}
              @dataSourceId="slr-contributions"
            >
              <ContributionHero
                @greeting={{this.greeting}}
                @firstName={{this.firstName}}
                @variant={{this.heroProps.variant}}
                @annualUsage={{this.heroProps.annualUsage}}
                @annualLimit={{this.heroProps.annualLimit}}
                @annualProgressPercent={{this.heroProps.annualProgressPercent}}
                @lifetimeUsage={{this.heroProps.lifetimeUsage}}
                @lifetimeLimit={{this.heroProps.lifetimeLimit}}
                @lifetimeProgressPercent={{this.heroProps.lifetimeProgressPercent}}
                @lifetimeContribution={{this.heroProps.lifetimeContribution}}
                @lifetimeAsOfDate={{this.heroProps.lifetimeAsOfDate}}
                @currentYear={{this.heroProps.currentYear}}
                @yearContribution={{this.heroProps.yearContribution}}
                @paymentCount={{this.heroProps.paymentCount}}
                @lastPaymentAmount={{this.heroProps.lastPaymentAmount}}
                @lastPaymentDate={{this.heroProps.lastPaymentDate}}
                @nextContribution={{this.heroProps.nextContribution}}
                @nextContributionDate={{this.heroProps.nextContributionDate}}
                @blocked={{this.heroProps.blocked}}
                @onViewBenefit={{this.openBenefitDrawer}}
              />
            </DataIndicator>
          {{/if}}

          {{! Info Banner for blocked states (PIF, limit reached) }}
          {{#if this.pageStateInfoBanner}}
            <div
              class="mt-6 tio-bg-warning-soft border tio-border-warning rounded-2xl p-5 tio-row-top-loose"
            >
              <div class="p-2 tio-bg-warning-soft rounded-lg shrink-0">
                {{#if (eq this.pageStateInfoBanner.icon "check")}}
                  <PhCheckCircle @size={{24}} class="tio-fg-warning" />
                {{else}}
                  <PhProhibit @size={{24}} class="tio-fg-warning" />
                {{/if}}
              </div>
              <div>
                <Heading @level={{3}} class="tio-fg-warning-body">
                  {{this.pageStateInfoBanner.title}}
                </Heading>
                <Text @size="sm" class="tio-fg-warning-body mt-1">
                  {{this.pageStateInfoBanner.description}}
                </Text>
              </div>
            </div>
          {{/if}}

          {{! Action Items — Needs Your Attention }}
          {{#if this.showActionItems}}
            {{#if this.actionItems.length}}
              <div class="mt-6 tio-col">
                <div class="tio-row-tight px-1">
                  <Heading @level={{3}} class="leading-none">
                    {{t "slr.reimagined.needs_your_attention"}}
                  </Heading>
                  <Text @weight="medium" class="tio-fg-secondary">
                    ({{this.actionItems.length}})
                  </Text>
                </div>

                <div class="tio-col-tight">
                  {{#each this.actionItems as |item|}}
                    {{#if item.tasks}}
                      <GroupedIssueNotificationCard
                        @item={{item}}
                        @person={{this.person}}
                        @onAddressSubmit={{this.handleAddressSubmit}}
                      />
                    {{else if (eq item.resolutionType "upload")}}
                      {{! SLR alerts requiring document upload (blank-account-number, no-loan-balance, blank-payment-address, all-pif) }}
                      <IssueNotificationCard
                        @title={{item.title}}
                        @description={{item.description}}
                        @person={{this.person}}
                        @successMessage={{or
                          item.successMessage
                          (t "slr.reimagined.successfully_added_loan_account")
                        }}
                      />
                    {{else if (eq item.resolutionType "contact-support")}}
                      {{! SLR alerts requiring support contact (partially-applied) }}
                      <div
                        class="tio-bg-surface border tio-border-default rounded-2xl px-4 py-6 text-left w-full"
                      >
                        <div class="tio-row-loose justify-between">
                          <div class="tio-row">
                            <div
                              class="tio-row-center rounded-lg shrink-0 size-12
                                {{this.getActionItemIconBg item}}"
                            >
                              <PhWarningCircle
                                @size={{32}}
                                class={{this.getActionItemIconColor item}}
                              />
                            </div>
                            <div class="tio-col-extra-tight">
                              <Heading
                                @level={{4}}
                                class="tio-fg-secondary capitalize tracking-tight"
                              >
                                {{item.title}}
                              </Heading>
                              <Text @size="sm" @weight="medium" class="tio-fg-muted">
                                {{item.description}}
                              </Text>
                            </div>
                          </div>
                          <LinkTo
                            @route="authenticated.contact-us"
                            class="text-sm font-medium tio-fg-info hover:tio-fg-info-body underline whitespace-nowrap shrink-0"
                          >
                            {{t "slr.reimagined.contact_support_link"}}
                          </LinkTo>
                        </div>
                      </div>
                    {{else if (eq item.resolutionType "dropdown")}}
                      {{! SLR alerts requiring address selection (blank-payment-address with MANUAL servicer) }}
                      <AddressSelectionCard
                        @title={{item.title}}
                        @description={{item.description}}
                        @addresses={{this.getAddressOptions item}}
                        @onSubmit={{fn this.handleAddressSubmit item}}
                        data-test-blank-payment-address-card
                      />
                    {{else if (eq item.id "upload-required")}}
                      {{! Legacy client-side upload alert }}
                      <IssueNotificationCard
                        @title={{item.title}}
                        @description={{item.description}}
                        @person={{this.person}}
                      />
                    {{else if (eq item.issueType "account-ineligible")}}
                      <IssueNotificationCard
                        @title={{item.title}}
                        @description={{item.description}}
                        @person={{this.person}}
                        @successMessage={{or
                          item.successMessage
                          (t "slr.reimagined.successfully_added_loan_account")
                        }}
                        data-test-account-ineligible-card
                      />
                    {{else if (eq item.issueType "account-setup")}}
                      <button
                        type="button"
                        class="tio-bg-surface border tio-border-default rounded-2xl px-4 py-6 text-left w-full cursor-pointer hover:tio-border-strong hover:shadow-sm transition-all"
                        {{on "click" (fn this.handleActionItemClick item)}}
                      >
                        <div class="tio-row-between">
                          <div class="tio-row">
                            <div
                              class="tio-row-center rounded-lg shrink-0 size-12
                                {{this.getActionItemIconBg item}}"
                            >
                              <PhLinkBreak
                                @size={{32}}
                                class={{this.getActionItemIconColor item}}
                              />
                            </div>
                            <div class="tio-col-extra-tight">
                              <div class="tio-row-tight">
                                <Heading
                                  @level={{4}}
                                  class="tio-fg-secondary capitalize tracking-tight"
                                >
                                  {{item.title}}
                                </Heading>
                                {{#if item.badge}}
                                  <Chip @intent={{item.badgeIntent}} @size="sm">
                                    {{item.badge}}
                                  </Chip>
                                {{/if}}
                              </div>
                              <Text @size="sm" @weight="medium" class="tio-fg-muted">
                                {{item.description}}
                              </Text>
                            </div>
                          </div>
                        </div>
                      </button>
                    {{else}}
                      <div
                        class="tio-bg-surface border tio-border-default rounded-2xl px-4 py-6 text-left w-full"
                      >
                        <div class="tio-row-between">
                          <div class="tio-row">
                            <div
                              class="tio-row-center rounded-lg shrink-0 size-12
                                {{this.getActionItemIconBg item}}"
                            >
                              {{#if (eq item.issueType "plan-requirement")}}
                                <PhShieldSlash
                                  @size={{32}}
                                  class={{this.getActionItemIconColor item}}
                                />
                              {{else}}
                                <PhWarningCircle
                                  @size={{32}}
                                  class={{this.getActionItemIconColor item}}
                                />
                              {{/if}}
                            </div>
                            <div class="tio-col-extra-tight">
                              <div class="tio-row-tight">
                                <Heading
                                  @level={{4}}
                                  class="tio-fg-secondary capitalize tracking-tight"
                                >
                                  {{item.title}}
                                </Heading>
                                {{#if item.badge}}
                                  <Chip @intent={{item.badgeIntent}} @size="sm">
                                    {{item.badge}}
                                  </Chip>
                                {{/if}}
                              </div>
                              <Text @size="sm" @weight="medium" class="tio-fg-muted">
                                {{item.description}}
                              </Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    {{/if}}
                  {{/each}}
                </div>
              </div>
            {{/if}}
          {{/if}}

          {{#if this.showFullDashboard}}
            {{! LRAP Opportunity Banner }}
            {{#if this.hasLrapOpportunity}}
              <DataIndicator
                @source={{this.lrapSource}}
                @showBadge={{true}}
                @dataSourceId="lrap-opportunity"
                class="mt-6"
              >
                <div class="app-hero-brand-wash rounded-xl p-5">
                  <div class="tio-row-top-loose">
                    <div class="p-3 tio-bg-brand-soft rounded-xl shrink-0">
                      <PhMagnifyingGlass @size={{28}} class="tio-fg-brand" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="tio-row-start-between flex-wrap gap-4">
                        <div>
                          <Heading @level={{3}} class="tio-fg-brand">
                            Additional Assistance Available!
                          </Heading>
                          <Text class="tio-fg-brand mt-1">
                            Based on your employer and loan profile, you may qualify for
                            {{#if this.lrapEnrolledCount}}
                              {{#if this.lrapEligibleCount}}
                                {{this.lrapEligibleCount}}
                                more loan repayment assistance programs.
                              {{else}}
                                ongoing assistance through LRAP programs.
                              {{/if}}
                            {{else}}
                              <strong>{{formatCurrency this.potentialLrapAmount}}/year</strong>
                              in Loan Repayment Assistance Programs.
                            {{/if}}
                          </Text>

                          {{#if this.hasOutsideFundsConflicts}}
                            <div
                              class="tio-row-tight mt-2 tio-fg-warning-body tio-bg-warning-soft px-3 py-1.5 rounded-lg w-fit"
                            >
                              <PhWarning @size={{16}} />
                              <Text @size="sm">
                                Optimization opportunity: Some programs have "outside funds" rules
                              </Text>
                            </div>
                          {{/if}}
                        </div>

                        <div class="tio-row">
                          {{#if this.lrapEnrolledCount}}
                            <Chip @intent="success" @size="sm">
                              {{this.lrapEnrolledCount}}
                              Enrolled
                            </Chip>
                          {{/if}}
                          {{#if this.lrapEligibleCount}}
                            <Chip @intent="primary" @size="sm">
                              {{this.lrapEligibleCount}}
                              Eligible
                            </Chip>
                          {{/if}}
                        </div>
                      </div>

                      <div class="tio-row mt-4">
                        <LinkTo @route="authenticated.reimagined.lrap">
                          <Button
                            @intent="primary"
                            @size="sm"
                            @icon={{PhArrowRight}}
                            @iconPlacement="end"
                            @label="Discover Programs"
                          />
                        </LinkTo>
                        {{#if this.hasOutsideFundsConflicts}}
                          <LinkTo @route="authenticated.reimagined.finance.optimizer">
                            <Button @intent="default" @size="sm" @appearance="outlined">
                              View Optimizer
                            </Button>
                          </LinkTo>
                        {{/if}}
                      </div>
                    </div>
                  </div>
                </div>
              </DataIndicator>
            {{/if}}

            {{! Key Metrics }}
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
              <DataIndicator
                @source={{this.studentLoansSource}}
                @showBadge={{true}}
                @dataSourceId="student-loans"
              >
                <StatCard
                  @icon={{PhBank}}
                  @label="Total Balance"
                  @value={{formatCurrency this.totalBalance}}
                  @color="indigo"
                >
                  <:extra>
                    <div class="tio-fg-success tio-row-extra-tight text-sm">
                      <PhTrendUp @size={{14}} />
                      <span>↓ {{formatCurrency this.yearProgressAmount}} this year</span>
                    </div>
                  </:extra>
                </StatCard>
              </DataIndicator>

              <DataIndicator
                @source={{this.studentLoansSource}}
                @showBadge={{true}}
                @dataSourceId="student-loans"
              >
                <StatCard
                  @icon={{PhCalendar}}
                  @label="Monthly Payment"
                  @value="{{formatCurrency this.totalMonthlyPayment}}/mo"
                  @subtext={{this.nextPaymentSubtext}}
                  @color="blue"
                />
              </DataIndicator>

              <DataIndicator
                @source={{this.contributionsSource}}
                @showBadge={{true}}
                @dataSourceId="slr-contributions"
              >
                <StatCard
                  @icon={{PhHandshake}}
                  @label="Employer Help"
                  @value="{{formatCurrency this.heroProps.nextContribution}}/mo"
                  @subtext="= $1,500/year tax-free!"
                  @color="green"
                  @valueClass="tio-fg-success"
                />
              </DataIndicator>

              <DataIndicator
                @source={{this.studentLoansSource}}
                @showBadge={{true}}
                @dataSourceId="student-loans"
              >
                <StatCard
                  @icon={{PhPercent}}
                  @label="Avg. Interest Rate"
                  @value="{{this.averageInterestRate}}%"
                  @color="amber"
                >
                  <:extra>
                    <div class="tio-fg-warning tio-row-extra-tight text-sm">
                      <PhWarning @size={{14}} />
                      <span>Refinance may lower this</span>
                    </div>
                  </:extra>
                </StatCard>
              </DataIndicator>
            </div>

            {{! Smart Actions }}
            <DataIndicator
              @source={{this.recommendationsSource}}
              @showBadge={{true}}
              @dataSourceId="ai-recommendations"
              class="mt-6"
            >
              <SectionHeader
                @icon={{PhLightbulb}}
                @title="Smart Actions"
                @iconClass="tio-fg-warning"
                @iconSize={{24}}
              />
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                {{#each this.actions as |smartAction|}}
                  <SmartActionCard @item={{smartAction}} />
                {{/each}}
              </div>
            </DataIndicator>
          {{/if}}

          {{#if this.showLoanAccounts}}
            {{! Loan Accounts Section }}
            <div class="mt-10">
              <div class="tio-bg-surface border tio-border-strong rounded-3xl">
                <div class="p-6">
                  <div class="tio-row-between mb-6">
                    <div class="tio-row-tight">
                      <PhBank @size={{20}} class="tio-fg-secondary" />
                      <Heading @level={{2}} @size="lg" class="font-medium">
                        Loan Accounts
                      </Heading>
                    </div>
                    <Button
                      @intent="default"
                      @appearance="outlined"
                      @size="md"
                      @label="Update"
                      {{on "click" this.toggleUploadDrawer}}
                    />
                  </div>

                  {{! Receiving Contributions }}
                  {{#if this.receivingLoans.length}}
                    <div class="tio-bg-sunken rounded-2xl p-4 mb-4">
                      <div class="tio-row-between px-1 mb-4">
                        <div class="tio-row-tight">
                          <Heading @level={{3}}>
                            Receiving Contributions
                          </Heading>
                        </div>
                        <button
                          type="button"
                          class="w-6 h-6 tio-row-center border-1.5 tio-border-info rounded"
                          aria-label="Edit receiving contributions"
                          {{on "click" this.toggleAccountDrawer}}
                        >
                          <PhPencilSimple @size={{16}} class="tio-fg-info" />
                        </button>
                      </div>

                      {{#each this.receivingLoans as |loan|}}
                        <div
                          class="tio-bg-surface border tio-border-strong rounded-2xl p-4 tio-col-tight mb-3 last:mb-0"
                        >
                          <div class="tio-row-between">
                            <div class="flex flex-col gap-1.5">
                              <div class="tio-row-tight">
                                <Heading @level={{4}}>
                                  {{loan.servicer}}
                                </Heading>
                                <Chip @intent="success" @size="sm">
                                  Active
                                </Chip>
                              </div>
                              <div class="flex items-center gap-1.5">
                                <Text @size="sm" @weight="medium" class="tio-fg-muted">
                                  {{formatCurrency (or loan.amountReceived 0)}}
                                  received
                                </Text>
                                <div class="w-1 h-1 rounded-full bg-gray-400"></div>
                                <Text @size="sm" @weight="medium" class="tio-fg-muted">
                                  {{or loan.contributionCount 0}}
                                  {{#if
                                    (eq loan.contributionCount 1)
                                  }}contribution{{else}}contributions{{/if}}
                                </Text>
                              </div>
                            </div>
                            <button
                              type="button"
                              {{on "click" (fn this.toggleAccountExpansion loan.id)}}
                              class="w-6 h-6"
                              aria-label={{if
                                (this.isAccountExpanded loan.id)
                                "Collapse account details"
                                "Expand account details"
                              }}
                              aria-expanded={{if (this.isAccountExpanded loan.id) "true" "false"}}
                            >
                              {{#if (this.isAccountExpanded loan.id)}}
                                <PhCaretUp @size={{24}} class="tio-fg-secondary" />
                              {{else}}
                                <PhCaretDown @size={{24}} class="tio-fg-secondary" />
                              {{/if}}
                            </button>
                          </div>

                          {{#if (this.isAccountExpanded loan.id)}}
                            <div class="mt-4 pt-4 border-t tio-border-default">
                              <div class="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <Text class="tio-fg-muted">Name</Text>
                                  <Text @weight="semibold">
                                    {{loan.name}}
                                  </Text>
                                </div>
                                <div>
                                  <Text class="tio-fg-muted">Account #</Text>
                                  <Text @weight="semibold">
                                    {{loan.accountNumber}}
                                  </Text>
                                </div>
                                <div>
                                  <Text class="tio-fg-muted">Servicer</Text>
                                  <Text @weight="semibold">
                                    {{loan.servicer}}
                                  </Text>
                                </div>
                                <div>
                                  <Text class="tio-fg-muted">Balance</Text>
                                  <Text @weight="semibold">
                                    {{formatCurrency loan.balance}}
                                  </Text>
                                </div>
                                <div class="col-span-2">
                                  <Text class="tio-fg-muted">Payment Address</Text>
                                  <Text @weight="semibold">
                                    {{loan.paymentAddress}}
                                  </Text>
                                </div>
                              </div>
                            </div>
                          {{/if}}
                        </div>
                      {{/each}}
                    </div>
                  {{/if}}

                  {{! Other Accounts }}
                  {{#if this.otherLoans.length}}
                    <div class="tio-col-loose">
                      <Heading @level={{3}} class="px-1">
                        Other
                      </Heading>
                      <div class="tio-bg-surface tio-col">
                        {{#each this.otherLoans as |loan|}}
                          <div
                            class="tio-bg-surface border tio-border-strong rounded-xl shadow-sm p-5 tio-row-between"
                          >
                            <div class="flex flex-col gap-1.5">
                              <div class="tio-row-tight">
                                <Heading @level={{4}}>
                                  {{loan.servicer}}
                                </Heading>
                                <Chip @intent="default" @size="sm">
                                  Inactive
                                </Chip>
                              </div>
                              <div class="flex items-center gap-1.5">
                                <Text @size="sm" @weight="medium" class="tio-fg-muted">
                                  {{formatCurrency (or loan.amountReceived 0)}}
                                  received
                                </Text>
                                <div class="w-1 h-1 rounded-full bg-gray-400"></div>
                                <Text @size="sm" @weight="medium" class="tio-fg-muted">No
                                  contributions</Text>
                              </div>
                            </div>
                            <button
                              type="button"
                              {{on "click" (fn this.toggleAccountExpansion loan.id)}}
                              class="w-6 h-6"
                              aria-label={{if
                                (this.isAccountExpanded loan.id)
                                "Collapse account details"
                                "Expand account details"
                              }}
                              aria-expanded={{if (this.isAccountExpanded loan.id) "true" "false"}}
                            >
                              {{#if (this.isAccountExpanded loan.id)}}
                                <PhCaretUp @size={{24}} class="tio-fg-secondary" />
                              {{else}}
                                <PhCaretDown @size={{24}} class="tio-fg-secondary" />
                              {{/if}}
                            </button>
                          </div>
                        {{/each}}
                      </div>
                    </div>
                  {{/if}}

                  {{! Empty state when no loan accounts }}
                  {{#unless (or this.receivingLoans.length this.otherLoans.length)}}
                    <div class="py-10 tio-place-center gap-3 text-center">
                      <Text @size="sm" class="tio-fg-muted">
                        No loan accounts connected yet.
                      </Text>
                    </div>
                  {{/unless}}
                </div>
              </div>
            </div>
          {{/if}}

          {{#if this.showContributionHistory}}
            {{! Contribution History Section }}
            <div class="mt-10">
              <div class="tio-row-tight px-1 mb-4">
                <PhCurrencyCircleDollar @size={{24}} class="tio-fg-secondary" />
                <Heading @level={{2}} @size="lg" class="font-medium">
                  {{t "slr.reimagined.contribution_history_title"}}
                </Heading>
              </div>

              {{#if this.contributionYears.length}}
                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  {{#each this.contributionYears as |yearData|}}
                    <div class="tio-bg-surface border tio-border-default rounded-2xl p-4 tio-col">
                      <div class="tio-row-between">
                        <Heading @level={{3}}>
                          {{t "slr.reimagined.year_contribution" year=yearData.year}}
                        </Heading>
                        <Text @size="lg" @weight="semibold">
                          {{formatCurrency yearData.total}}
                        </Text>
                      </div>

                      {{#if yearData.accounts.length}}
                        <div class="tio-col-extra-tight">
                          <Text
                            @size="xs"
                            @weight="medium"
                            class="tio-fg-muted uppercase tracking-wide"
                          >
                            {{t "slr.reimagined.account_label" count=yearData.accounts.length}}
                          </Text>
                          {{#each yearData.accounts as |account|}}
                            <div class="tio-row-between">
                              <Text @size="sm" class="tio-fg-secondary">
                                {{account.name}}
                              </Text>
                              <Text @size="sm" @weight="medium" class="tio-fg-secondary">
                                {{formatCurrency account.amount}}
                              </Text>
                            </div>
                          {{/each}}
                        </div>
                      {{/if}}

                      <div class="tio-row pt-1 border-t tio-border-subtle">
                        {{#if yearData.sentCount}}
                          <Text @size="xs" class="tio-fg-muted">
                            {{t "slr.reimagined.sent_count" count=yearData.sentCount}}
                          </Text>
                        {{/if}}
                        {{#if yearData.returnedCount}}
                          <Text @size="xs" class="tio-fg-danger">
                            {{t "slr.reimagined.returned_count" count=yearData.returnedCount}}
                          </Text>
                        {{/if}}
                        {{#if yearData.resentCount}}
                          <Text @size="xs" class="tio-fg-warning">
                            {{t "slr.reimagined.resent_count" count=yearData.resentCount}}
                          </Text>
                        {{/if}}
                      </div>
                    </div>
                  {{/each}}
                </div>
              {{else}}
                <div
                  class="tio-bg-surface border tio-border-default rounded-2xl py-10 tio-place-center gap-2 text-center"
                >
                  <Text @size="sm" class="tio-fg-muted">
                    {{t "slr.reimagined.no_payment_history"}}
                  </Text>
                </div>
              {{/if}}
            </div>
          {{/if}}

          {{#if this.showFullDashboard}}
            {{! Milestones }}
            <DataIndicator
              @source={{this.milestonesSource}}
              @showBadge={{true}}
              @dataSourceId="debt-milestones"
              class="mt-6"
            >
              <MilestoneTracker
                @milestones={{this.milestones}}
                @currentProgress={{this.progressPercent}}
              />
            </DataIndicator>

            {{! Financial Tools }}
            <div class="mt-6">
              <SectionHeader
                @icon={{PhChartLineUp}}
                @title="Financial Tools"
                @iconClass="tio-fg-info"
                @iconSize={{24}}
              />
              <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
                <NavCard
                  @layout="gradient"
                  @route="authenticated.repaying-student-debt.repayment-strategy-finder"
                  @icon={{PhTarget}}
                  @title="Strategy Finder"
                  @subtitle="Best repayment path"
                  @intent="info"
                />
                <NavCard
                  @layout="gradient"
                  @route="authenticated.reimagined.coaches"
                  @icon={{PhChatCircleDots}}
                  @title="Loan Coaches"
                  @subtitle="Expert guidance"
                  @intent="brand"
                />
                <NavCard
                  @layout="gradient"
                  @route="authenticated.reimagined.glossary"
                  @icon={{PhBookOpen}}
                  @title="Glossary"
                  @subtitle="Learn the terms"
                  @intent="info"
                />
                <NavCard
                  @layout="gradient"
                  @route="authenticated.reimagined.finance.optimizer"
                  @icon={{PhSparkle}}
                  @title="127 Optimizer"
                  @subtitle="Tax-free allocation"
                  @intent="success"
                />
                <NavCard
                  @layout="gradient"
                  @route="authenticated.idr.dashboard"
                  @icon={{PhCurrencyDollar}}
                  @title="Income-Driven"
                  @subtitle="Lower payments"
                  @intent="warning"
                />
              </div>
            </div>

            {{! Employer Banner }}
            {{#if this.isDemoMode}}
              <DataIndicator
                @source={{this.contributionsSource}}
                @showBadge={{true}}
                @dataSourceId="slr-contributions"
                class="mt-6"
              >
                <div
                  class="app-hero-band-success app-hero-band-success--strong rounded-xl p-6 tio-fg-inverse"
                >
                  <div class="tio-row-between flex-wrap gap-4">
                    <div class="tio-row-loose">
                      <div class="p-3 bg-white/20 rounded-lg">
                        <PhHandshake @size={{28}} />
                      </div>
                      <div>
                        <Heading @level={{3}} class="tio-fg-inverse">
                          {{this.employerName}}
                          — $4,850 applied toward your loans
                        </Heading>
                        <p class="tio-fg-on-brand">
                          Part of your benefits package — 2 years closer to payoff since you
                          enrolled.
                        </p>
                      </div>
                    </div>
                    <LinkTo @route="authenticated.reimagined.finance.history">
                      <Button
                        @intent="primary"
                        @appearance="outlined"
                        class="border-white tio-fg-inverse hover:bg-white/10"
                      >
                        View Contribution History
                      </Button>
                    </LinkTo>
                  </div>
                </div>
              </DataIndicator>
            {{/if}}
          {{/if}}
        {{else}}
          {{! NO LOANS LINKED YET }}
          <DataIndicator @source={{this.studentLoansSource}} @showBadge={{true}}>
            <div class="app-hero-info-wash rounded-2xl p-8 text-center">
              <div class="w-20 h-20 tio-bg-info-soft rounded-full tio-row-center mx-auto mb-6">
                <PhCloudArrowUp @size={{40}} class="tio-fg-info" />
              </div>

              <Heading @level={{1}} class="app-page-title mb-3">
                Upload Your Student Loan Documents
              </Heading>

              <Text class="tio-fg-secondary max-w-md mx-auto mb-6">
                Upload your loan statements or NSLDS file to see your balances, track your progress,
                and start receiving employer contributions of up to
                {{formatCurrency this.monthlyContribution}}/month!
              </Text>

              <div class="flex flex-col sm:flex-row gap-4 justify-center">
                <LinkTo
                  @route="authenticated.observability.upload"
                  @query={{hash
                    source="reimagined-loans"
                    providers="nslds,statement"
                    returnTo="authenticated.reimagined.loans"
                  }}
                  class="tio-btn tio-btn--primary tio-btn--sm gap-2"
                >
                  <PhCloudArrowUp @size={{18}} />
                  <span>Upload Loan Documents</span>
                </LinkTo>
                <Button @intent="primary" @appearance="outlined">
                  Learn More
                </Button>
              </div>

              <div class="mt-6 bg-white/70 rounded-xl p-4 max-w-md mx-auto">
                <Text @size="sm" class="tio-fg-muted">
                  Accepted: NSLDS files from StudentAid.gov, loan servicer statements, or any
                  document showing your loan details.
                </Text>
              </div>

              <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                <div class="tio-bg-surface rounded-xl p-4">
                  <div class="tio-fg-info text-2xl font-bold">
                    {{formatCurrency this.monthlyContribution}}
                  </div>
                  <div class="text-sm tio-fg-secondary">Monthly employer contribution</div>
                </div>
                <div class="tio-bg-surface rounded-xl p-4">
                  <div class="tio-fg-success text-2xl font-bold">2.5 years</div>
                  <div class="text-sm tio-fg-secondary">Average payoff acceleration</div>
                </div>
                <div class="tio-bg-surface rounded-xl p-4">
                  <div class="tio-fg-brand text-2xl font-bold">
                    {{formatCurrency this.lifetimeBenefit}}
                  </div>
                  <div class="text-sm tio-fg-secondary">Lifetime employer benefit</div>
                </div>
              </div>
            </div>
          </DataIndicator>
        {{/if}}
      {{else}}
        {{! LOAN BENEFIT NOT AVAILABLE - Friendly redirect to education benefits }}
        <div class="app-hero-brand-wash rounded-2xl p-8">
          <div class="max-w-2xl mx-auto text-center">
            <div class="w-20 h-20 app-brand-circle rounded-full tio-row-center mx-auto mb-6">
              <PhGraduationCap @size={{40}} class="tio-fg-brand" />
            </div>

            <Heading @level={{1}} class="app-page-title mb-3">
              Focus on Your Education Instead!
            </Heading>

            <Text class="tio-fg-secondary mb-8">
              While student loan repayment isn't part of your current benefits package, you have
              access to generous tuition assistance that can help you grow your career and avoid
              future debt.
            </Text>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div class="app-tile-on-brand rounded-xl p-5">
                <div class="text-3xl font-bold tio-fg-brand mb-1">$5,250</div>
                <div class="text-sm tio-fg-secondary">Annual tuition assistance</div>
              </div>
              <div class="app-tile-on-brand rounded-xl p-5">
                <div class="text-3xl font-bold tio-fg-success mb-1">Tax-Free</div>
                <div class="text-sm tio-fg-secondary">Section 127 benefit</div>
              </div>
              <div class="app-tile-on-brand rounded-xl p-5">
                <div class="text-3xl font-bold tio-fg-info mb-1">100+</div>
                <div class="text-sm tio-fg-secondary">Approved programs</div>
              </div>
            </div>

            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <LinkTo
                @route="authenticated.reimagined.benefits"
                class="tio-btn tio-btn--brand tio-btn--lg gap-2 rounded-xl"
              >
                <PhGraduationCap @size={{20}} />
                <span>Explore Education Benefits</span>
              </LinkTo>
              <LinkTo
                @route="authenticated.reimagined.explore"
                class="tio-inline-row-center gap-2 px-6 py-3 tio-bg-surface tio-fg-brand font-semibold rounded-xl border tio-border-brand-soft hover:bg-[var(--brand-wash)] transition-colors"
              >
                <PhTarget @size={{20}} />
                <span>Explore Careers</span>
              </LinkTo>
            </div>

            <div class="mt-8 p-4 app-tile-on-brand-translucent rounded-xl">
              <div class="tio-row-tight justify-center text-sm tio-fg-secondary">
                <PhLightbulb @size={{18}} class="tio-fg-warning" />
                <span>
                  <strong>Pro tip:</strong>
                  Earning a degree or certification can increase your salary by 20-30%
                </span>
              </div>
            </div>
          </div>
        </div>
      {{/if}}
      {{! Account Selector Drawer }}
      <AccountSelectorDrawer
        @isOpen={{this.isAccountDrawerOpen}}
        @onClose={{this.toggleAccountDrawer}}
        @accounts={{@model.activeAccounts}}
        @currentAccount={{@model.accountReceivingContributions}}
        @personId={{@model.personId}}
        @onAccountChanged={{this.onAccountChanged}}
      />
      {{! Document Upload Drawer }}
      <DocumentUploadDrawer
        @isOpen={{this.isUploadDrawerOpen}}
        @onClose={{this.toggleUploadDrawer}}
        @person={{this.sessionContext.person}}
      />
      {{! Benefit Detail Drawer }}
      <BenefitDetailDrawer
        @isOpen={{this.isBenefitDrawerOpen}}
        @onClose={{this.closeBenefitDrawer}}
        @plan={{this.plan}}
      />
    </VStack>
  </template>
}
